/* eslint-disable react/destructuring-assignment */
/* eslint-disable no-console */
/* eslint-disable @typescript-eslint/ban-ts-comment */
import React, { Component, ReactNode } from "react";
import { Box, Button, Heading, Text, VStack } from "@chakra-ui/react";
import { parseError, AppError } from "@/types/errors";

interface ErrorBoundaryProps {
  children: ReactNode;
  fallback?: (error: AppError, resetError: () => void) => ReactNode;
  onError?: (error: AppError, errorInfo: React.ErrorInfo) => void;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: AppError | null;
}

/**
 * Error Boundary component to catch React component errors
 * Prevents entire app from crashing when a component throws an error
 */
class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
    };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    const appError = parseError(error);
    return {
      hasError: true,
      error: appError,
    };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    const appError = parseError(error);

    // Log to console in development
    if (process.env.NODE_ENV === "development") {
      console.error("ErrorBoundary caught an error:", error);
      console.error("Error info:", errorInfo);
      console.error("Component stack:", errorInfo.componentStack);
    }

    // Call custom error handler if provided
    if (this.props.onError) {
      this.props.onError(appError, errorInfo);
    }

    // Log to Sentry in production
    if (process.env.NODE_ENV === "production" && typeof window !== "undefined") {
      // @ts-ignore - Sentry will be available after installation
      if (window.Sentry) {
        // @ts-ignore
        window.Sentry.captureException(error, {
          contexts: {
            react: {
              componentStack: errorInfo.componentStack,
            },
          },
        });
      }
    }
  }

  resetError = () => {
    this.setState({
      hasError: false,
      error: null,
    });
  };

  render() {
    if (this.state.hasError && this.state.error) {
      // Use custom fallback if provided
      if (this.props.fallback) {
        return this.props.fallback(this.state.error, this.resetError);
      }

      // Default fallback UI
      return (
        <Box
          minH="100vh"
          display="flex"
          alignItems="center"
          justifyContent="center"
          bg="gray.50"
          p={4}
        >
          <VStack
            spacing={6}
            maxW="600px"
            bg="white"
            p={8}
            borderRadius="lg"
            boxShadow="lg"
          >
            <Heading size="lg" color="red.500">
              Nastala chyba
            </Heading>
            <Text textAlign="center" color="gray.600">
              Aplikácia narazila na neočakávaný problém. Ospravedlňujeme sa za
              nepríjemnosti.
            </Text>
            {process.env.NODE_ENV === "development" && (
              <Box
                w="100%"
                p={4}
                bg="red.50"
                borderRadius="md"
                borderLeft="4px solid"
                borderColor="red.500"
              >
                <Text fontSize="sm" fontWeight="bold" color="red.700" mb={2}>
                  Debug Info (Development only):
                </Text>
                <Text fontSize="xs" fontFamily="mono" color="red.600">
                  {this.state.error.message}
                </Text>
                {this.state.error.originalError && (
                  <Text
                    fontSize="xs"
                    fontFamily="mono"
                    color="red.600"
                    mt={2}
                    whiteSpace="pre-wrap"
                  >
                    {this.state.error.originalError.stack}
                  </Text>
                )}
              </Box>
            )}
            <VStack spacing={3} w="100%">
              <Button
                colorScheme="blue"
                size="lg"
                w="100%"
                onClick={this.resetError}
              >
                Skúsiť znova
              </Button>
              <Button
                variant="outline"
                size="lg"
                w="100%"
                onClick={() => {
                  window.location.href = "/";
                }}
              >
                Späť na úvodnú stránku
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  window.location.reload();
                }}
              >
                Obnoviť stránku
              </Button>
            </VStack>
          </VStack>
        </Box>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
